import React, { useEffect } from "react";
import styles from "../sections/sectionstyle.module.css";
import { IoIosArrowRoundBack, IoIosArrowRoundForward } from "react-icons/io";
import {
	Backdrop,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { useSubmitSurveyData, useValidateCode } from "../../../api/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { useSurveyFormContext } from "../../../context/survey-form-data/SurverFormContext";
import toast from "react-hot-toast";
import Demographics from "../sections/Demographics";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { combinedSchema } from "../../../validators/mis/demographics";
import { useSurveySectionContent } from "../../../context/survey-track/SurveySectionContext";
import { misSurveySection } from "../../../utils/constants";
import Thankyou from "../sections/thank-you/Thankyou";
import RadioButtons from "../RadioInput";
import CheckBoxesInput from "../CheckBoxInput";
import {
	childrenReceivedSMCOptions,
	childrenReceiveVaccineOptions,
	firstDoseOptions,
	firstSeekMalariaTreatmentOptions,
	healthcareTransportCostOptions,
	lastDrugPurchaseTime,
	malariaDrugCostOptions,
	mosquitoRepellantsOptions,
	nearestHealthcareOptions,
	testCostOptions,
} from "../../../utils/mis-survey-options";
import { processSurveyData } from "../../../utils/helpers";
import Banner from "../sections/banner/Banner";

const MISSurveyCall = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { currentSurveySection, dispatch } = useSurveySectionContent();
	const { surveyFormData, dispatch: dispatchSurvey } = useSurveyFormContext();
	const sessionSurveyCode = sessionStorage.getItem("::dc::us-mis") || "";
	const sessionAgentNumber = sessionStorage.getItem("::agent::us-mis") || "";
	const sessionChannel = sessionStorage.getItem("::channel::us-mis") || "";
	const {
		data,
		isError,
		isSuccess,
		isLoading: isValidating,
		error,
	} = useValidateCode(sessionSurveyCode, true);

	const handlePrevious = () => {
		dispatch({
			type: "UPDATE",
			payload: {
				currentSection: misSurveySection.DEMOGRAPHICS,
				showConfetti: false,
				formCompletedAt: "",
				rewardPrice: "",
			},
		});
	};

	const { submitSurvey, isLoading } = useSubmitSurveyData({
		onSuccess: () => {
			dispatch({
				type: "UPDATE",
				payload: {
					currentSection: misSurveySection.THANK_YOU,
					showConfetti: false,
					formCompletedAt: null,
				},
			});
		},
		onError: (error) => {
			const errorMessage = error?.response?.data?.message;
			if (Array.isArray(errorMessage) && errorMessage.length > 1) {
				toast.error("An error occured, please try again");
			} else {
				toast.error(
					errorMessage || "unable to submit survey, please try again"
				);
			}
		},
	});

	const {
		control,
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(combinedSchema),
		defaultValues: surveyFormData?.surveyResponses,
	});

	const onSubmit = handleSubmit((data) => {
		const completeData = { ...surveyFormData?.surveyResponses, ...data };

		processSurveyData(completeData, (responses) => {
			submitSurvey({
				survey_id: surveyFormData?.survey_id,
				channel: surveyFormData?.channel,
				agent: surveyFormData?.agent,
				survey_code: surveyFormData?.survey_code,
				respondent: surveyFormData?.respondent,
				response: responses,
			});
		});
	});

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const urlSurveyCode = queryParams.get("c") ?? "";
		const agentNumber = queryParams.get("userId") ?? "";
		const surveyChannel = queryParams.get("channel") ?? "";
		if (urlSurveyCode) {
			sessionStorage.setItem("::dc::us-mis", urlSurveyCode);
			sessionStorage.setItem("::agent::us-mis", agentNumber);
			sessionStorage.setItem("::channel::us-mis", surveyChannel);
			navigate(`${location.pathname}?${queryParams.toString()}`, {
				replace: true,
			});
		}
	}, [location, navigate]);


	useEffect(() => {
		if (isSuccess && data) {
			dispatchSurvey({
				type: "UPDATE_RESPONDENT",
				payload: {
					...surveyFormData?.respondent,
					phone_number: data.data?.phone_number,
				},
			});

			setTimeout(() => {
				dispatchSurvey({
					type: "UPDATE_SURVEY_PARAMS",
					payload: {
                        ...surveyFormData,
						survey_id: data.data?.survey_id,
						channel:
							sessionChannel && sessionChannel !== ""
								? sessionChannel
								: "outbound",
						agent:
							sessionAgentNumber && sessionAgentNumber !== ""
								? Number(sessionAgentNumber)
								: null,
						survey_code: data.data?.survey_code,
					},
				});
			}, 1000);

			setTimeout(() => {
                if (!Boolean(currentSurveySection?.currentSection))
					dispatch({
						type: "UPDATE",
						payload: {
							currentSection: misSurveySection.DEMOGRAPHICS,
							showConfetti: false,
							formCompletedAt: null,
						},
					});
			}, 1500);
		}

		if (isError) {
			toast.error(
				error?.response?.data?.message ||
					"You do not have access to this survey"
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, isError, isSuccess]);

	const formData = watch();

	const hasOtherRepellants = formData?.quiz_8?.includes("39");
	const hasOtherTransportCost = formData?.quiz_11?.includes("67");
	const hasOtherTestCost = formData?.quiz_14?.includes("91");
	const hasOtherMalariaCost = formData?.quiz_15?.includes("109");

	useEffect(() => {
		if (!hasOtherRepellants) {
			setValue("quiz_8_other_39", "");
		}
		if (!hasOtherTransportCost) {
			setValue("quiz_11_other_67", "");
		}
		if (!hasOtherTestCost) {
			setValue("quiz_14_other_91", "");
		}
		if (!hasOtherMalariaCost) {
			setValue("quiz_15_other_109", "");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		hasOtherRepellants,
		hasOtherTransportCost,
		hasOtherTestCost,
		hasOtherMalariaCost,
	]);

	return (
		<section className={styles.container}>
			<Banner
				title={"MIS Survey"}
				description={
					"We need a few details to understand the respondent background"
				}
			/>
			<Backdrop
				sx={(theme) => ({
					color: "#fff",
					zIndex: theme.zIndex.drawer + 3,
				})}
				open={!isError && isValidating}
			>
				<CircularProgress color="inherit" />
			</Backdrop>

			{currentSurveySection?.currentSection ===
				misSurveySection.DEMOGRAPHICS && <Demographics />}

			{currentSurveySection?.currentSection ===
				misSurveySection.PREVENTION && (
				<form onSubmit={onSubmit} style={{ marginTop: "0" }}>
					{/* PREVENTION SECTION */}

					<Controller
						name="quiz_5"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="Did you or anyone in your household sleep under a mosquito net last night?"
								items={[
									{ option_text: "Yes", option_id: 26 },
									{ option_text: "No", option_id: 27 },
									{
										option_text: "I don't know",
										option_id: 28,
									},
								]}
								{...(register("quiz_5"), { required: true })}
								hasError={Boolean(errors.quiz_5)}
								errorMessage={errors?.quiz_5?.message || ""}
							/>
						)}
					/>
					{formData.quiz_5 === "26" && (
						<Controller
							name="quiz_6"
							control={control}
							render={({ field }) => (
								<RadioButtons
									field={field}
									question="Do you know if that net is an insecticide treated net?"
									items={[
										{
											option_text: "Yes -  It is",
											option_id: 29,
										},
										{
											option_text: "No - It is not",
											option_id: 30,
										},
										{
											option_text: "I don't know",
											option_id: 31,
										},
									]}
									{...(register("quiz_6"),
									{ required: true })}
									hasError={Boolean(errors.quiz_6)}
									errorMessage={errors?.quiz_6?.message || ""}
								/>
							)}
						/>
					)}

					<Controller
						name="quiz_7"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="In the past 12 months, has anyone entered your home to spray the interior walls for mosquito control?"
								items={[
									{ option_text: "Yes", option_id: 32 },
									{ option_text: "No", option_id: 33 },
									{
										option_text: "I don't know what it is ",
										option_id: 34,
									},
								]}
								{...(register("quiz_7"), { required: true })}
								hasError={Boolean(errors.quiz_7)}
								errorMessage={errors?.quiz_7?.message || ""}
							/>
						)}
					/>

					<div>
						<Controller
							name="quiz_8"
							control={control}
							render={({ field }) => (
								<CheckBoxesInput
									field={field}
									setValue={setValue}
									question="Do you use any of these mosquito repellent methods?"
									items={mosquitoRepellantsOptions}
									{...(register("quiz_8"),
									{ required: true })}
									hasError={Boolean(errors.quiz_8)}
									errorMessage={errors?.quiz_8?.message || ""}
								/>
							)}
						/>
						{formData?.quiz_8?.includes("39") && (
							<div className={styles.other_text_input_wrapper}>
								<small
									style={{
										display: "block",
										marginBottom: "8px",
									}}
								>
									State the other types of mosquito repellent
									methods you use
								</small>
								<TextField
									id="quiz_8_other_39"
									label="enter other repellants here"
									{...register("quiz_8_other_39", {
										required: true,
									})}
									variant="outlined"
									size="small"
									className={styles.other_text_input}
								/>
								{errors.quiz_8_other_39 && (
									<small className="text-danger">
										{errors.quiz_8_other_39.message}
									</small>
								)}
							</div>
						)}
					</div>

					<div className={styles.form_education}>
						<p>
							Where do you usually go first to seek malaria
							treatment?
						</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_9_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_9"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_9_label"
										id="quiz_9"
										variant="filled"
										size="small"
									>
										{firstSeekMalariaTreatmentOptions.map(
											(level) => (
												<MenuItem
													key={level.option_id}
													value={level.option_id.toString()}
												>
													{level.option_text}
												</MenuItem>
											)
										)}
									</Select>
								)}
							/>
							{errors.quiz_9 && (
								<small className="text-danger">
									{errors.quiz_9.message}
								</small>
							)}
						</FormControl>
					</div>

					<div className={styles.form_education}>
						<p>
							How long does it take to reach the nearest
							healthcare facility to seek malaria treatment?
						</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_10_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_10"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_10_label"
										id="quiz_10"
										variant="filled"
										size="small"
									>
										{nearestHealthcareOptions.map(
											(level) => (
												<MenuItem
													key={level.option_id}
													value={level.option_id.toString()}
												>
													{level.option_text}
												</MenuItem>
											)
										)}
									</Select>
								)}
							/>
							{errors.quiz_10 && (
								<small className="text-danger">
									{errors.quiz_10.message}
								</small>
							)}
						</FormControl>
					</div>

					<div className={styles.form_education}>
						<p>
							How much do you spend on transport cost (to and fro)
							to visit the nearest healthcare facility to seek
							malaria treatment?
						</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_11_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_11"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_11_label"
										id="quiz_11"
										variant="filled"
										size="small"
									>
										{healthcareTransportCostOptions.map(
											(level) => (
												<MenuItem
													key={level.option_id}
													value={level.option_id.toString()}
												>
													{level.option_text}
												</MenuItem>
											)
										)}
									</Select>
								)}
							/>
							{errors.quiz_11 && (
								<small className="text-danger">
									{errors.quiz_11.message}
								</small>
							)}
						</FormControl>
						{formData?.quiz_11?.includes("67") && (
							<div className={styles.other_text_input_wrapper}>
								<TextField
									id="quiz_11_other_67"
									label="enter other prices here"
									{...register("quiz_11_other_67", {
										required: true,
									})}
									variant="outlined"
									size="small"
									className={styles.other_text_input}
								/>
								{errors.quiz_11_other_67 && (
									<small className="text-danger">
										{errors.quiz_11_other_67.message}
									</small>
								)}
							</div>
						)}
					</div>

					{/* TREATMENT SECTION */}

					<Controller
						name="quiz_12"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="Have you or anyone in your household had fever in the last 2 weeks?"
								items={[
									{ option_text: "Yes", option_id: 69 },
									{ option_text: " No", option_id: 70 },
									{
										option_text: "I don't know",
										option_id: 71,
									},
								]}
								{...(register("quiz_12"), { required: true })}
								hasError={Boolean(errors.quiz_12)}
								errorMessage={errors?.quiz_12?.message || ""}
							/>
						)}
					/>

					{formData.quiz_12 === "69" && (
						<Controller
							name="quiz_13"
							control={control}
							render={({ field }) => (
								<RadioButtons
									field={field}
									question="Was a blood sample taken from any part of their body for testing before treatment was given?"
									items={[
										{ option_text: "Yes", option_id: 72 },
										{ option_text: "No", option_id: 73 },
										{
											option_text: "I don't know",
											option_id: 74,
										},
									]}
									{...(register("quiz_13"),
									{ required: true })}
									hasError={Boolean(errors.quiz_13)}
									errorMessage={
										errors?.quiz_13?.message || ""
									}
								/>
							)}
						/>
					)}

					{formData.quiz_13 === "72" && (
						<div className={styles.form_education}>
							<p>How much did the test cost you?</p>
							<FormControl fullWidth>
								<InputLabel id="quiz_14_label">
									Choose one
								</InputLabel>
								<Controller
									name="quiz_14"
									control={control}
									render={({ field }) => (
										<Select
											{...field}
											labelId="quiz_14_label"
											id="quiz_14"
											variant="filled"
											size="small"
										>
											{testCostOptions.map((level) => (
												<MenuItem
													key={level.option_id}
													value={level.option_id.toString()}
												>
													{level.option_text}
												</MenuItem>
											))}
										</Select>
									)}
								/>
							</FormControl>
							{errors.quiz_14 && (
								<small className="text-danger">
									{errors.quiz_14.message}
								</small>
							)}

							{formData?.quiz_14?.includes("91") && (
								<div
									className={styles.other_text_input_wrapper}
								>
									<TextField
										id="quiz_14_other_91"
										label="enter other prices here"
										{...register("quiz_14_other_91", {
											required: true,
										})}
										variant="outlined"
										size="small"
										className={styles.other_text_input}
									/>
									{errors.quiz_14_other_91 && (
										<small className="text-danger">
											{errors.quiz_14_other_91.message}
										</small>
									)}
								</div>
							)}
						</div>
					)}

					<div className={styles.form_education}>
						<p>How much did your last malaria drugs cost?</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_15_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_15"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_15_label"
										id="quiz_15"
										variant="filled"
										size="small"
									>
										{malariaDrugCostOptions.map((level) => (
											<MenuItem
												key={level.option_id}
												value={level.option_id.toString()}
											>
												{level.option_text}
											</MenuItem>
										))}
									</Select>
								)}
							/>
						</FormControl>
						{errors.quiz_15 && (
							<small className="text-danger">
								{errors.quiz_15.message}
							</small>
						)}

						{formData?.quiz_15?.includes("109") && (
							<div className={styles.other_text_input_wrapper}>
								<TextField
									id="quiz_15_other_109"
									label="enter other prices here"
									{...register("quiz_15_other_109", {
										required: true,
									})}
									variant="outlined"
									size="small"
									className={styles.other_text_input}
								/>
								{errors.quiz_15_other_109 && (
									<small className="text-danger">
										{errors.quiz_15_other_109.message}
									</small>
								)}
							</div>
						)}
					</div>

					<div className={styles.form_education}>
						<p>When did you buy your last malaria drug?</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_16_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_16"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_16_label"
										id="quiz_16"
										variant="filled"
										size="small"
									>
										{lastDrugPurchaseTime.map((level) => (
											<MenuItem
												key={level.option_id}
												value={level.option_id.toString()}
											>
												{level.option_text}
											</MenuItem>
										))}
									</Select>
								)}
							/>
							{errors.quiz_16 && (
								<small className="text-danger">
									{errors.quiz_16.message}
								</small>
							)}
						</FormControl>
					</div>

					<Controller
						name="quiz_17"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="Rate the affordability of malaria drugs in your community’ "
								items={[
									{
										option_text: "Very affordable",
										option_id: 116,
									},
									{
										option_text: "Somewhat affordable",
										option_id: 117,
									},
									{
										option_text:
											"Neutral - Neither affordable nor expensive Somewhat expensive",
										option_id: 118,
									},
									{
										option_text: "Very expensive",
										option_id: 119,
									},
								]}
								{...(register("quiz_17"), { required: true })}
								hasError={Boolean(errors.quiz_17)}
								errorMessage={errors?.quiz_17?.message || ""}
							/>
						)}
					/>

					{/* FEEDBACK SECTION */}

					<Controller
						name="quiz_18"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="Have you heard about Seasonal Malaria Chemoprevention (SMC)?"
								extraInfo="i.e chidren are given doses of antimalaria drugs periodically"
								items={[
									{ option_text: "Yes", option_id: 120 },
									{ option_text: "No", option_id: 121 },
								]}
								{...(register("quiz_18"), { required: true })}
								hasError={Boolean(errors.quiz_18)}
								errorMessage={errors?.quiz_18?.message || ""}
							/>
						)}
					/>

					<div className={styles.form_education}>
						<p>
							Have your children ever received Seasonal Malaria
							Chemoprevention (SMC) treatment?
						</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_19_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_19"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_19_label"
										id="quiz_19"
										variant="filled"
										size="small"
									>
										{childrenReceivedSMCOptions.map(
											(level) => (
												<MenuItem
													key={level.option_id}
													value={level.option_id.toString()}
												>
													{level.option_text}
												</MenuItem>
											)
										)}
									</Select>
								)}
							/>
						</FormControl>
						{errors.quiz_19 && (
							<small className="text-danger">
								{errors.quiz_19.message}
							</small>
						)}
					</div>

					<div className={styles.form_education}>
						<p>
							At what age should a child receive the first dose of
							the malaria vaccine?
						</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_20_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_20"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_20_label"
										id="quiz_20"
										variant="filled"
										size="small"
									>
										{firstDoseOptions.map((level) => (
											<MenuItem
												key={level.option_id}
												value={level.option_id.toString()}
											>
												{level.option_text}
											</MenuItem>
										))}
									</Select>
								)}
							/>
						</FormControl>
						{errors.quiz_20 && (
							<small className="text-danger">
								{errors.quiz_20.message}
							</small>
						)}
					</div>

					<div className={styles.form_education}>
						<p>
							Have your children ever received a malaria vaccine
							dose?
						</p>
						<FormControl fullWidth>
							<InputLabel id="quiz_21_label">
								Choose one
							</InputLabel>
							<Controller
								name="quiz_21"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										labelId="quiz_21_label"
										id="quiz_21"
										variant="filled"
										size="small"
									>
										{childrenReceiveVaccineOptions.map(
											(level) => (
												<MenuItem
													key={level.option_id}
													value={level.option_id.toString()}
												>
													{level.option_text}
												</MenuItem>
											)
										)}
									</Select>
								)}
							/>
						</FormControl>
						{errors.quiz_21 && (
							<small className="text-danger">
								{errors.quiz_21.message}
							</small>
						)}
					</div>

					<Controller
						name="quiz_22"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="Have you received free malaria treatment at a government facility in the last 6 months?"
								items={[
									{ option_text: "Yes", option_id: 137 },
									{
										option_text:
											"No and I went for in malaria treatment in a government health facility in the last 6 months",
										option_id: 138,
									},
									{
										option_text:
											" No, I did not go for malaria treatment in a government health facility in the last 6 months ",
										option_id: 139,
									},
								]}
								{...(register("quiz_22"), { required: true })}
								hasError={Boolean(errors.quiz_22)}
								errorMessage={errors?.quiz_22?.message || ""}
							/>
						)}
					/>

					<Controller
						name="quiz_23"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="Have you experienced any case where there was no malaria drugs available at a government health facilities in the last 6 months?"
								items={[
									{ option_text: "Yes", option_id: 140 },
									{
										option_text:
											"No and I went for in malaria treatment in a government health facility in the last 6 months",
										option_id: 141,
									},
									{
										option_text:
											" No, I did not go for malaria treatment in a government health facility in the last 6 months ",
										option_id: 142,
									},
								]}
								{...(register("quiz_23"), { required: true })}
								hasError={Boolean(errors.quiz_23)}
								errorMessage={errors?.quiz_23?.message || ""}
							/>
						)}
					/>

					<Controller
						name="quiz_24"
						control={control}
						render={({ field }) => (
							<RadioButtons
								field={field}
								question="How would you rate the government's efforts in combating malaria?"
								items={[
									{
										option_text: "Very effective",
										option_id: 143,
									},
									{
										option_text: "Somewhat effective",
										option_id: 144,
									},
									{ option_text: "Neutral", option_id: 145 },
									{
										option_text: "Somewhat ineffective",
										option_id: 146,
									},
									{
										option_text: "Very ineffective",
										option_id: 147,
									},
								]}
								{...(register("quiz_24"), { required: true })}
								hasError={Boolean(errors.quiz_24)}
								errorMessage={errors?.quiz_24?.message || ""}
							/>
						)}
					/>

					<div className={styles.button_wrapper}>
						<button
							type="button"
							onClick={handlePrevious}
							disabled={isLoading}
						>
							<IoIosArrowRoundBack color="white" size={24} />
							Previous{" "}
						</button>
						<button type="submit" disabled={isLoading}>
							{isLoading ? "Submitting" : "Submit"}
							<IoIosArrowRoundForward color="white" size={24} />
						</button>
					</div>
				</form>
			)}

			{currentSurveySection?.currentSection ===
				misSurveySection.THANK_YOU && <Thankyou />}
		</section>
	);
};

export default MISSurveyCall;
